/**
 * API Service - MyLojic Customer Portal
 *
 * Version History:
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40: onboardingService.connectSkip() -> POST /api/onboarding/connect-skip (saves the
 *                      "skipped for now" note; the server logs it and returns its request id).
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01): request interceptor adds X-Tenant-Id = the chosen company's
 *                      tenantUuid; the five per-call X-Tenant-Id overrides (own company key) are removed so the choice is
 *                      the one source; 403 not_a_member / 409 company_choice_required are announced; invite validation
 *                      and accept types carry matchesSignedIn, maskedEmail, tenantUuid and the honest error codes
 * v4.27 - 2026-10-07 - INDUSTRY-CODE-NOT-STORED FU-1: setupService.complete reads the 409 not-ready object from
 *                      `detail` (FastAPI HTTPException shape), falling back to the flat body
 * v4.26 - 2026-10-06 - INDUSTRY-CODE-NOT-STORED (S2, S6b, S8): onboardingService.naicsStatus / naicsResolve;
 *                      SetupNotReadyReason gains INDUSTRY_CODE_MISSING
 * v4.22 - 2026-10-02 - CONNECTOR-HEALTH-STATE-H3: per-connector-type health types and getHealth removed (F85)
 * v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2: Connection/ConnectionInfo gain healthState/healthReason (+ escalationStatus);
 *                      AIRBYTE_SYNC_FAILED reason; 409 `details` and the AIR-04 sync-status service removed
 * v2.99 - 2026-06-05 - Added plansService + Tier interface for /api/plans catalog (STORY8-STEP3)
 * v2.98 - 2026-02-07 - Added webhookService for custom webhooks
 * v2.97 - 2026-02-07 - Added reportService for report builder
 * v2.94 - 2026-02-07 - Added notificationService for notification center
 * v2.89 - 2026-02-06 - Fixed ProposalsResponse field name (proposals not mappings); fixed stats shape
 * v2.88 - 2026-02-06 - Added proposalService, approvalModeService, setupService.resume; approval phase types
 * v2.80 - 2026-02-05 - Added startOAuthPopup for popup-based OAuth (keeps main window history clean)
 * v2.74 - 2026-02-05 - Use X-Authorization header (SWA intercepts standard Authorization)
 * v2.56 - 2026-02-04 - Added Clerk session token to Authorization header for BFF auth
 * v2.55 - 2026-02-04 - Updated 401 handling to redirect to Clerk login page (/login)
 * v2.45 - 2026-02-02 - Added dashboardKPIService for KPI cards and Power BI embed
 * v2.28 - 2026-01-30 - Added billingService for Stripe subscription management
 * v2.27 - 2026-01-30 - Added teamService and settingsService for team management and settings
 * v2.26 - 2026-01-30 - Added authService for credential forms and delegation
 * v2.25 - 2026-01-30 - Added mode parameter to discovery start for quick/full discovery
 * v2.22 - 2026-01-25 - Added connectorsService for dynamic connector catalog
 * v2.6 - 2026-01-23 - BFF pattern for pipelineService (tenantId resolved server-side)
 * v2.5 - 2026-01-23 - BFF pattern for discoveryService (tenantId resolved server-side)
 * v2.4 - 2026-01-23 - Fixed discovery API calls
 *
 * IMPORTANT: Azure SWA intercepts and overwrites the standard Authorization header.
 * We use X-Authorization instead. See:
 * - https://github.com/Azure/static-web-apps/issues/158
 * - https://github.com/Azure/static-web-apps/issues/275
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { failClosedClerkSession, getClerkSessionId } from "./authGate";
// v4.28 - 2026-10-13 - G10-B: the chosen company rides on every Axios request (same choice as portalRequest)
import i18n from '../i18n'; // v4.28 - G10-B: the 503 "try again" text comes from the translation file
import { getChosenCompany, classifyCompanyAccess, reportCompanyAccess, COMPANY_REQUEST_HEADER } from "./portalRequest";
// v3.58 - Import chart types locally (needed by ChartDataResponse); re-exported at line ~1125 - 2026-02-26
import type { ColumnDefinition, VisualizationMetadata, ChartReportData } from '../utils/chartUtils';
import type { HealthState } from '../utils/connectionHealthDisplay'; // v4.21 - CONNECTOR-HEALTH-STATE-H2

// v2.56 - Clerk token getter function - set by ClerkTokenProvider component
let getClerkToken: (() => Promise<string | null>) | null = null;

export const setClerkTokenGetter = (getter: () => Promise<string | null>) => {
  getClerkToken = getter;
};

// TypeScript Interfaces
export interface ChatRequest {
  message: string;
  sessionId?: string;
}

export interface ChatResponse {
  answer: string;
  sessionId: string;
}

// G08: /api on the customer custom domain is owned by Front Door and routes directly to d75-agent.
// The legacy SWA BFF is not deployed. Browser requests carry the Clerk token for d75 zero-trust auth.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: true  // Include cookies for SWA session
});

// v2.74 / G08 - add Clerk session token for direct d75 authentication
// IMPORTANT: Use X-Authorization instead of Authorization because Azure SWA
// intercepts and overwrites the standard Authorization header.
// See: https://github.com/Azure/static-web-apps/issues/158
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    (config as InternalAxiosRequestConfig & { portalSessionId: string | null }).portalSessionId = getClerkSessionId();
    // v4.28 - G10-B: the chosen company on EVERY request (never overrides an explicit header)
    const chosenCompany = getChosenCompany();
    if (chosenCompany && !config.headers[COMPANY_REQUEST_HEADER]) {
      config.headers[COMPANY_REQUEST_HEADER] = chosenCompany;
    }
    // Add correlation ID for request tracking
    config.headers['X-Correlation-ID'] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // v2.74 - Add Clerk session token to X-Authorization header (not Authorization!)
    // Azure SWA intercepts Authorization header and replaces it with its own internal token
    if (getClerkToken) {
      try {
        const token = await getClerkToken();
        if (token) {
          config.headers['X-Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get Clerk token:', error);
      }
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
    }
    return response;
  },
  async (error: AxiosError) => {
    const errorMessage = getErrorMessage(error);
    console.error(`❌ API Error:`, errorMessage, error);

    // v4.28 - G10-B: 403 not_a_member / 409 company_choice_required are announced to the layout.
    // The portal log call itself is excluded so a refused log can never loop.
    if (error.response && !String(error.config?.url || '').startsWith('/portal/log')) {
      const accessKind = classifyCompanyAccess(error.response.status, error.response.data);
      if (accessKind) reportCompanyAccess(accessKind, String(error.config?.url || ''));
      if (accessKind === 'unavailable') {
        return Promise.reject({ ...error, userMessage: i18n.t('teamAccess.unavailable') });
      }
    }

    // v2.55 - If 401, redirect to Clerk login page
    if (error.response?.status === 401) {
      await failClosedClerkSession(undefined, undefined,
        (error.config as (InternalAxiosRequestConfig & { portalSessionId?: string | null }) | undefined)?.portalSessionId);
      return Promise.reject(error);
    }

    return Promise.reject({
      ...error,
      userMessage: errorMessage
    });
  }
);

function getErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return "Request timed out. Please check your connection and try again.";
    }
    return "Network error. Please check your internet connection.";
  }

  const status = error.response.status;
  const data = error.response.data as any;

  if (data?.userMessage) {
    return data.userMessage;
  }

  if (data?.message) {
    return data.message;
  }

  switch (status) {
    case 400:
      return "Invalid request. Please check your input.";
    case 401:
      return "Authentication required. Please sign in again.";
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return "Resource not found.";
    case 429:
      return "Too many requests. Please try again later.";
    case 500:
      return "Server error. Please try again later.";
    case 502:
    case 503:
      return "Service temporarily unavailable. Please try again.";
    default:
      return `An error occurred (${status}). Please try again.`;
  }
}

// Chat Service
export const chatService = {
  sendMessage: async (message: string, sessionId?: string): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>("/chat", {
      message,
      sessionId
    });
    return response.data;
  }
};

// Dashboard KPIs Service
export interface KPI {
  value: number | null;
  change: number | null;
  label: string;
  available: boolean;
}

export interface KPIsResponse {
  success: boolean;
  tenant_id: string;
  kpis: {
    revenue: KPI;
    customers: KPI;
    orders: KPI;
    avgOrderValue: KPI;
  };
  data_status: {
    goldTablesReady: boolean;
    silverMappingsApproved: number;
    silverMappingsPending: number;
    connectionsActive: number;
    lastUpdated: string | null;
  };
}

export const dashboardService = {
  getKPIs: async (tenantId: string): Promise<KPIsResponse> => {
    const response = await api.get<KPIsResponse>("/gold/kpis");
    return response.data;
  }
};

// Connections Service
export interface Connection {
  id: string;
  sourceType: string;
  companyName: string | null;
  status: string;
  connectedAt: string | null;
  lastSyncAt: string | null;
  syncStatus: string | null;
  // v2.43 - Added for error tooltip display
  // v4.21 - CONNECTOR-HEALTH-STATE-H2: still projected (H2 R-2) but no longer read by the UI — the tooltip
  // shows healthReason.
  lastSyncResult?: {
    entities?: Record<string, number>;
    total_records?: number;
    errors?: string[] | null;
    syncedAt?: string;
  };
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-1/H2-2; F16, F17, F18): the server's one verdict for this connection
  // (render it through utils/connectionHealthDisplay), and the `status` of the escalation report_issue wrote.
  // airbyteConnectionId (v3.66 AIR-04 chip gate) removed: never projected, and the chip is retired.
  healthState?: HealthState;
  healthReason?: string | null;
  escalationStatus?: string;
}

export interface ConnectionsResponse {
  status: string;
  tenantId: string;
  connections: Connection[];
}

export const connectionsService = {
  list: async (tenantId: string): Promise<ConnectionsResponse> => {
    const response = await api.get<ConnectionsResponse>("/connections");
    return response.data;
  },

  startOAuth: (connectorType: string, tenantId: string, extraParams?: Record<string, string>) => {
    const params = new URLSearchParams({ tenant_id: tenantId, ...extraParams });
    // v3.95 - Session 2 moved d75-agent auth router from /api/connect to /api/auth - 2026-03-30
    window.location.href = `/api/auth/${connectorType}/start?${params.toString()}`;
  },

  // v2.80 - Open OAuth in popup window to keep main window history clean - 2026-02-05
  startOAuthPopup: (connectorType: string, tenantId: string, extraParams?: Record<string, string>): Window | null => {
    const params = new URLSearchParams({ tenant_id: tenantId, return_to: '/oauth-callback', ...extraParams });
    // v3.95 - Session 2 moved d75-agent auth router from /api/connect to /api/auth - 2026-03-30
    const url = `/api/auth/${connectorType}/start?${params.toString()}`;
    const popup = window.open(url, 'oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
    return popup;
  },

  getStatus: async (connectionId: string, tenantId: string) => {
    const response = await api.get(`/connections/${connectionId}/status`);
    return response.data;
  },

  // v2.41 - Trigger manual data sync - 2026-02-02
  sync: async (connectionId: string, tenantId: string) => {
    const response = await api.post(`/connections/${connectionId}/sync`, null);
    return response.data;
  },

  // v3.95 - PF-03: Disconnect a data source - 2026-04-04
  delete: async (connectionId: string, tenantId: string): Promise<void> => {
    await api.delete(`/connections/${connectionId}`);
  }
};

// Upload Service (stub)
export const dataService = {
  uploadCSV: async (file: File): Promise<any> => {
    throw new Error('Upload functionality not yet available. Backend API will be implemented in later groups.');
  }
};

// Discovery Service - Conversational onboarding
// v2.54 - Added skipped and summary fields for skip mode
export interface DiscoveryStartResponse {
  session_id: string;
  status: string;
  current_objective: string | null;
  progress: number;
  question: string | null;
  suggestions: string[];
  mode?: string;
  skipped?: boolean;
  summary?: Record<string, string>;
  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-10: per-field origin on skip-start and resume (d75 10.88)
  summaryOrigin?: Record<string, 'default' | 'basic_info' | 'customer'>;
}

// v4.18 - DISCOVERY-LIFECYCLE F02b - 2026-09-15: the resume shape, which is what /open
// returns AND what /start returns for an existing open session. `resumed` has been on the
// wire since d75-agent 10.36 with no declaration; `started_at` is new in 10.37 and is the
// session's start (discoveryState.start_time, else created_at) so the wizard can continue
// its clock instead of restarting it.
export interface DiscoveryOpenResponse extends DiscoveryStartResponse {
  resumed?: boolean;
  started_at?: string | null;
}

export interface DiscoveryAnswerResponse {
  status: string;
  current_objective: string;
  phase?: string;
  progress: number;
  question: string;
  suggestions?: string[];
  probing_item?: string;
  items?: string[];
  next_step?: string;
  // v2.25 - Added for review status response
  summary?: Record<string, string>;
  message?: string;
  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-10: per-field origin on /skip (d75 10.88)
  summaryOrigin?: Record<string, 'default' | 'basic_info' | 'customer'>;
}

export interface DiscoveryConfirmResponse {
  status: string;
  tenantId: string;
  discovery: {
    status: string;
    completedAt: string;
    industry: string;
    companySize: string;
    challenges: string;
    dataSources: string;
    painPoints: string;
    decisionMakers: string;
    successMetrics: string;
    sessionId: string;
  };
}

// v2.32 - Removed unused DiscoveryStatusResponse interface

// Discovery Service - G08 canonical path: browser /api -> Front Door -> d75-agent.
// d75 derives tenant identity from the verified Clerk JWT.
// v2.25 - Added mode parameter for quick (5min) vs full (15min) discovery
// v2.48 - Added skip mode option - 2026-02-03
export interface DiscoveryStartOptions {
  mode?: 'quick' | 'full' | 'skip';
}

export const discoveryService = {
  // v4.18 - F02b: start can return the resumed shape, so it is typed as such.
  start: async (options?: DiscoveryStartOptions): Promise<DiscoveryOpenResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    // v2.25 - Pass mode to backend for quick vs full discovery
    const response = await api.post<DiscoveryOpenResponse>('/discovery/start', {
      mode: options?.mode || 'quick'
    });
    return response.data;
  },

  answer: async (sessionId: string, objective: string, answer: string): Promise<DiscoveryAnswerResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.post<DiscoveryAnswerResponse>(
      `/discovery/${sessionId}/answer`,
      { objective, answer }
    );
    return response.data;
  },

  next: async (sessionId: string): Promise<DiscoveryStartResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.get<DiscoveryStartResponse>(`/discovery/${sessionId}/next`);
    return response.data;
  },

  // v4.16 - DISCOVERY-LIFECYCLE F02 - 2026-09-15: the tenant's open discovery session.
  // Tenant-addressed, so the wizard can recover a session id it no longer holds -- the id
  // lived only in component state, so a refresh lost it and the only remaining action was
  // to start again, which orphaned the previous row permanently.
  // Resolves to null on 404 (nothing open); any other error propagates.
  open: async (): Promise<DiscoveryOpenResponse | null> => {
    try {
      const response = await api.get<DiscoveryOpenResponse>('/discovery/open');
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  confirm: async (sessionId: string, edits?: Record<string, string>): Promise<DiscoveryConfirmResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.post<DiscoveryConfirmResponse>(
      `/discovery/${sessionId}/confirm`,
      { edits: edits || {} }
    );
    return response.data;
  },

  // v2.48 - Skip discovery and save defaults - 2026-02-03
  skip: async (sessionId: string): Promise<DiscoveryAnswerResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.post<DiscoveryAnswerResponse>(
      `/discovery/${sessionId}/skip`,
      {}
    );
    return response.data;
  }
};

// Connectors Service - Dynamic connector catalog
// v2.23 - Added connectorStatus and connection fields - 2026-01-30
export interface ConnectionInfo {
  id: string;
  status: string;
  syncStatus: string | null;
  companyName: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-2): same verdict as /api/connections for the same row
  healthState?: HealthState;
  healthReason?: string | null;
}

// v3.95 - Pre-auth param definition for connectors requiring user input before OAuth - 2026-04-06
export interface PreAuthParam {
  key: string;
  label: string;
  placeholder?: string;
  suffix?: string;
  required?: boolean;
  pattern?: string;
  patternError?: string;
}

export interface ConnectorInfo {
  connectorType: string;
  displayName: string;
  description: string;
  category: string;
  icon: string;
  authType: string;
  connectorStatus: 'available' | 'coming_soon' | 'beta' | 'maintenance';
  connection: ConnectionInfo | null;
  // v3.60 - NAICS-aware priority score from connector_priority_index (0.0 = no data) - 2026-03-04
  priorityScore?: number;
  // v3.95 - Pre-auth params for frontend modal (shop_domain, region, etc.) - 2026-04-06
  preAuthParams?: PreAuthParam[];
}

export interface ConnectorsForTenantResponse {
  status: string;
  tenantId: string;
  discovery: {
    dataSources: string;
    industry: string;
  };
  matched: ConnectorInfo[];
  recommended: ConnectorInfo[];
  other: Record<string, ConnectorInfo[]>;
}

export const connectorsService = {
  getForTenant: async (): Promise<ConnectorsForTenantResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    // v2.23 - Now calls with-connections endpoint
    const response = await api.get<ConnectorsForTenantResponse>('/connectors');
    return response.data;
  }
};

// Pipeline Service - Data processing status
// G08: these calls use the canonical direct d75 /api contract.
export interface PipelineStatusResponse {
  success: boolean;
  job_id: string;
  status: string;
  started_at?: string;
  ended_at?: string;
  failure_reason?: string;
}

export const pipelineService = {
  getDiscoveryStatus: async (jobId: string): Promise<PipelineStatusResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.get<PipelineStatusResponse>(`/pipeline/discover/${jobId}/status`);
    return response.data;
  },

  getTransformStatus: async (jobId: string): Promise<PipelineStatusResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.get<PipelineStatusResponse>(`/pipeline/transform/${jobId}/status`);
    return response.data;
  },

  getAggregateStatus: async (runId: string): Promise<PipelineStatusResponse> => {
    // d75 derives tenantId from the verified Clerk JWT
    const response = await api.get<PipelineStatusResponse>(`/pipeline/aggregate/${runId}/status`);
    return response.data;
  }
};

// ============================================================================
// Auth Service - Credential forms and delegation (v2.26)
// ============================================================================

// Form field from backend schema
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'email';
  required: boolean;
  placeholder?: string;
  help_text?: string;
}

// Form schema from backend
export interface FormSchema {
  title: string;
  description: string;
  connector_type: string;
  fields: FormField[];
  submit_label: string;
  cancel_label: string;
}

// Response from /auth/{connector}/form or /auth/{connector}/start
export interface AuthFormResponse {
  flow_type: 'form' | 'redirect';
  form_schema?: FormSchema;
  state_id?: string;
  message?: string;
  start_url?: string;  // For redirect flow
}

// Response from /auth/{connector}/credentials
export interface CredentialSubmitResponse {
  success: boolean;
  connection_id?: string;
  connector_type?: string;
  redirect_url?: string;
  error?: string;
}

// Delegation request
export interface DelegationRequest {
  connectorType: string;
  delegateEmail: string;
  message?: string;
}

// Delegation response
export interface DelegationResponse {
  success: boolean;
  requestId?: string;
  expiresAt?: string;
  error?: string;
}

// Delegation status (for landing page)
export interface DelegationStatusResponse {
  valid: boolean;
  connectorType?: string;
  connectorDisplayName?: string;
  tenantName?: string;
  requesterEmail?: string;
  expiresAt?: string;
  error?: string;
}

export const authService = {
  // Get form schema for a connector (API Key or Basic Auth)
  getFormSchema: async (connectorType: string): Promise<AuthFormResponse> => {
    const response = await api.get<AuthFormResponse>(`/auth/${connectorType}/form`);
    return response.data;
  },

  // Submit credentials for API Key or Basic Auth connectors
  submitCredentials: async (
    connectorType: string,
    credentials: Record<string, string>
  ): Promise<CredentialSubmitResponse> => {
    const response = await api.post<CredentialSubmitResponse>(
      `/auth/${connectorType}/credentials`,
      { connector_type: connectorType, ...credentials }
    );
    return response.data;
  },

  // Get auth info for a connector (to determine flow type)
  getAuthInfo: async (connectorType: string): Promise<{
    connector_type: string;
    auth_type: string;
    flow_type: 'form' | 'redirect';
    requires_redirect: boolean;
    requires_form: boolean;
  }> => {
    const response = await api.get(`/auth/${connectorType}/info`);
    return response.data;
  },

  // Create a delegation request (send connection link to accountant)
  createDelegation: async (request: DelegationRequest): Promise<DelegationResponse> => {
    const response = await api.post<DelegationResponse>('/delegation/create', request);
    return response.data;
  },

  // Get delegation status by token (for landing page)
  getDelegationStatus: async (token: string): Promise<DelegationStatusResponse> => {
    const response = await api.get<DelegationStatusResponse>(`/delegation/${token}/status`);
    return response.data;
  },

  // Complete delegation (delegate calls this after OAuth)
  completeDelegation: async (token: string): Promise<{ success: boolean; error?: string }> => {
    const response = await api.post(`/delegation/${token}/complete`);
    return response.data;
  }
};

// ============================================================================
// Team Service - Team management (v2.27)
// ============================================================================

export interface TeamMember {
  id: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'pending';
  invitedAt?: string;
  joinedAt?: string;
  avatarUrl?: string;
}

export interface TeamInvite {
  id: string;
  email: string;
  role: string;
  invitedAt: string;
  expiresAt: string;
  invitedBy: string;
  expired: boolean;
}

export interface TeamMembersResponse {
  status: string;
  members: TeamMember[];
}

export interface TeamInvitesResponse {
  status: string;
  invites: TeamInvite[];
}

// v4.28 - G10-B: no role in the request - the server applies the invitable role from its policy file
export interface InviteRequest {
  email: string;
  message?: string;
}

export interface InviteResponse {
  success: boolean;
  memberId?: string;
  status?: string;
  expiresAt?: string;
  error?: string;
}

export interface InviteValidationResponse {
  valid: boolean;
  role?: string;
  // v4.28 - G10-B: signed out the server returns only companyName, role and expiresAt
  companyName?: string;
  expiresAt?: string;
  // v4.28 - G10-B (row 609): present only when the call carried a valid sign-in
  matchesSignedIn?: boolean;
  maskedEmail?: string;
  // v4.28 - G10-B (final G10-A contract, gap 3): the invitation's id (never the link or code); logged with every decision
  inviteId?: string;
  // v4.29 - INVITE-ACCEPT-FLOW-01 (B.2): the server's answer about whether an account exists for
  // the invited email. The portal reads it to decide which primary button to show on the invite
  // page without a ticket (true → Sign in to accept; false → Create an account to accept). Absent
  // on the pre-session-401 server; the invite page falls back to the sign-in-first default.
  accountExists?: boolean;
  reason?: string;
  error?: string;
}

// v4.28 - G10-B (row 609): what an invite page shows, decided from the server's answer and whether a person is signed in.
// Pure function so the decision is testable without rendering.
export type InviteView = 'invalid' | 'stop' | 'accept' | 'sign_in';
export const inviteViewFor = (result: InviteValidationResponse | null, isSignedIn: boolean): InviteView => {
  if (!result || !result.valid) return 'invalid';
  if (!isSignedIn) return 'sign_in';
  // The server compared the invite email with the signed-in email. Only an explicit "no" stops the person.
  return result.matchesSignedIn === false ? 'stop' : 'accept';
};

// The /register route for a signed-in person on a pilot or team invite link.
export type InviteGateView = 'stop' | 'account_exists' | 'go_to_invite' | 'not_valid';
export const inviteGateViewFor = (kind: 'pilot' | 'team', result: { valid: boolean; matchesSignedIn?: boolean } | null): InviteGateView => {
  if (!result || !result.valid) return 'not_valid';
  if (result.matchesSignedIn === false) return 'stop';
  if (result.matchesSignedIn === true) return kind === 'team' ? 'go_to_invite' : 'account_exists';
  return 'not_valid'; // no comparison came back (not signed in at the server): never guess a match
};

// v4.29 - INVITE-ACCEPT-FLOW-01 (B.2): which button is primary on the invite page without a ticket,
// decided from the server's accountExists. `exists_unknown` is for the pre-session-401 server that
// does not carry the field; the invite page falls back to the sign-in-first default there.
export type InvitePrimary = 'sign_in' | 'create_account' | 'exists_unknown';
export const invitePrimaryFor = (result: { accountExists?: boolean } | null): InvitePrimary => {
  if (!result) return 'exists_unknown';
  if (result.accountExists === true) return 'sign_in';
  if (result.accountExists === false) return 'create_account';
  return 'exists_unknown';
};

// v4.28 - G10-B: accept success carries the invited company; failures answer {success:false, error:<code>}
export interface InviteAcceptResponse {
  success: boolean;
  tenantUuid?: string;
  tenantId?: string;
  role?: string;
  error?: string;
}

// v4.28 - G10-B (row 609): the pilot invite check; with a sign-in it also answers matchesSignedIn and maskedEmail
export interface PilotInviteCheck {
  valid: boolean;
  inviteId?: string;
  reason?: string;
  matchesSignedIn?: boolean;
  maskedEmail?: string;
  // v4.29 - INVITE-ACCEPT-FLOW-01 (B.2): the server's answer about whether an account exists for
  // the invited email. Shape mirrors InviteValidationResponse.accountExists.
  accountExists?: boolean;
}

export const pilotInviteService = {
  validate: async (code: string, authToken?: string | null): Promise<PilotInviteCheck> => {
    const response = await api.post<PilotInviteCheck>('/invitations/validate', { code }, {
      headers: authToken ? { 'X-Authorization': `Bearer ${authToken}` } : undefined,
    });
    return response.data;
  },
};

export const teamService = {
  // Get all team members
  getMembers: async (): Promise<TeamMembersResponse> => {
    const response = await api.get<TeamMembersResponse>('/team');
    return response.data;
  },

  // Get pending invites
  getPendingInvites: async (): Promise<TeamInvitesResponse> => {
    const response = await api.get<TeamInvitesResponse>('/team/invites');
    return response.data;
  },

  // Invite a new team member
  invite: async (request: InviteRequest): Promise<InviteResponse> => {
    const response = await api.post<InviteResponse>('/team/invite', request);
    return response.data;
  },

  // Cancel a pending invite
  cancelInvite: async (inviteId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/team/invites/${inviteId}`);
    return response.data;
  },

  // Resend an invite
  resendInvite: async (inviteId: string): Promise<{ success: boolean; expiresAt?: string }> => {
    const response = await api.post(`/team/invites/${inviteId}/resend`);
    return response.data;
  },

  // Update member role
  updateRole: async (memberId: string, role: string): Promise<{ success: boolean; role?: string }> => {
    const response = await api.put(`/team/${memberId}/role`, { role });
    return response.data;
  },

  // Remove a team member
  removeMember: async (memberId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/team/${memberId}`);
    return response.data;
  },

  // Validate an invite token (for invite landing page)
  // v4.28 - G10-B: authToken (a signed-in person's Clerk token) makes the server also answer matchesSignedIn and
  // maskedEmail; sent explicitly because the page can load before the global token getter is set.
  validateInvite: async (token: string, authToken?: string | null): Promise<InviteValidationResponse> => {
    const response = await api.get<InviteValidationResponse>(`/team/invite/${token}/validate`, {
      headers: authToken ? { 'X-Authorization': `Bearer ${authToken}` } : undefined,
    });
    return response.data;
  },

  // Accept an invite (after login/signup)
  acceptInvite: async (token: string): Promise<InviteAcceptResponse> => {
    const response = await api.post(`/team/invite/${token}/accept`);
    return response.data;
  }
};

// ============================================================================
// Settings Service - Account settings and preferences (v2.27)
// ============================================================================

export interface NotificationSettings {
  email_sync_complete: boolean;
  email_errors: boolean;
  email_weekly_summary: boolean;
  email_team_updates: boolean;
}

export interface Settings {
  companyName: string;
  industry: string;
  timezone: string;
  discoveryMode: 'quick' | 'full';
  notifications: NotificationSettings;
}

export interface SettingsResponse {
  status: string;
  settings: Settings;
}

export interface SettingsUpdateRequest {
  companyName?: string;
  industry?: string;
  timezone?: string;
  discoveryMode?: 'quick' | 'full';
  notifications?: Partial<NotificationSettings>;
}

export const settingsService = {
  // Get all settings
  get: async (): Promise<SettingsResponse> => {
    const response = await api.get<SettingsResponse>('/settings');
    return response.data;
  },

  // Update settings
  update: async (request: SettingsUpdateRequest): Promise<{ success: boolean }> => {
    const response = await api.put('/settings', request);
    return response.data;
  },

  // Request account deletion
  requestDeletion: async (): Promise<{ success: boolean; message?: string }> => {
    const response = await api.delete('/settings/account');
    return response.data;
  },

  // v2.31 - Validate deletion token (for confirm deletion page)
  validateDeletionToken: async (token: string): Promise<{
    valid: boolean;
    tenantId?: string;
    email?: string;
    expiresAt?: string;
    error?: string;
  }> => {
    const response = await api.get(`/settings/deletion/${token}/validate`);
    return response.data;
  },

  // v2.31 - Confirm account deletion
  confirmDeletion: async (token: string): Promise<{ success: boolean; error?: string }> => {
    const response = await api.post(`/settings/deletion/${token}/confirm`);
    return response.data;
  }
};

// ============================================================================
// Billing Service - Stripe subscription management (v2.28)
// ============================================================================

// v3.01 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: widen with three hidden caps (nlp_qpm,
// nlp_questions_monthly, doc_pages_monthly) as optional so existing fixtures still
// compile; owner decision to show all seven limits on subscription tiers - 2026-09-13
export interface PlanFeatures {
  connections: number | null;
  team_members: number | null;
  api_calls_monthly: number | null;
  dashboards: number | null;
  nlp_qpm?: number | null;
  nlp_questions_monthly?: number | null;
  doc_pages_monthly?: number | null;
  support: string;
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  price_yearly?: number;
  features: PlanFeatures;
  // v3.01 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: backend surfaces payment_mode so the
  // Billing page can hide the feature grid for one-time-purchase tiers - 2026-09-13
  payment_mode?: string;
}

export interface Usage {
  connections: number;
  team_members: number;
  api_calls_this_month: number;
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: number;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
}

export interface Subscription {
  status: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  billing_cycle?: string;
}

export interface BillingResponse {
  status: string;
  plan: Plan;
  subscription?: Subscription;
  usage: Usage;
  invoices: Invoice[];
  available_plans: Record<string, Plan>;
}

export interface CheckoutRequest {
  plan: string; // v3.00 - STORY-10E-C: catalog-driven; server validates tier_id
  billing_cycle?: 'monthly' | 'yearly';
  success_url?: string;
  cancel_url?: string;
}

export interface CheckoutResponse {
  success: boolean;
  checkout_url?: string;
  session_id?: string;
  error?: string;
}

export interface PortalResponse {
  success: boolean;
  portal_url?: string;
  error?: string;
}

export const billingService = {
  // Get billing status (current plan, usage, invoices)
  getStatus: async (): Promise<BillingResponse> => {
    const response = await api.get<BillingResponse>('/billing');
    return response.data;
  },

  // Create Stripe checkout session for upgrade
  createCheckout: async (request: CheckoutRequest): Promise<CheckoutResponse> => {
    const response = await api.post<CheckoutResponse>('/billing/checkout', request);
    return response.data;
  },

  // Create Stripe portal session for managing subscription
  createPortal: async (): Promise<PortalResponse> => {
    const response = await api.post<PortalResponse>('/billing/portal');
    return response.data;
  }
};

// ============================================================================
// Plans Service - Plan catalog (v2.99)
// v2.99 - 2026-06-05 - Public plan catalog for pricing display (STORY8-STEP3)
// v3.00 - 2026-06-10 - STORY-10E-C: add sort_order + payment_mode to Tier; CheckoutRequest.plan → string
// ============================================================================

// v3.01 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: widen with three hidden caps as optional
// so existing fixtures still compile; production enforces seven caps - 2026-09-13
export interface TierLimits {
  connections: number | null;
  team_members: number | null;
  api_calls_monthly: number | null;
  dashboards: number | null;
  nlp_qpm?: number | null;
  nlp_questions_monthly?: number | null;
  doc_pages_monthly?: number | null;
}

export interface Tier {
  tier_id: string;
  display_name: string;
  price_monthly: number;
  currency: string;
  setup_fee: number;
  setup_credit_months: number;
  credit_forfeit_on_cancel: boolean;
  limits: TierLimits;
  aliases: string[];
  sort_order: number;    // v3.00 - STORY-10E-C: tier ordering; drives upgrade-path logic
  payment_mode: string;  // v3.00 - STORY-10E-C: 'subscription' | 'payment'; filter non-subscription rows
}

export const plansService = {
  // v2.99 - Fetch public plan catalog (no auth required)
  getPlans: async (): Promise<Tier[]> => {
    const response = await api.get<{ plans: Tier[] }>('/plans');
    return response.data.plans;
  },
};

// Setup Service - Onboarding completion and pipeline triggering (v2.34)
// v2.34 - 2026-01-31 - Added for finish setup and pipeline status polling

// v2.47 - Added error field for pipeline failure responses - 2026-02-03
export interface SetupCompleteResponse {
  status: string;
  tenantId: string;
  pipelineState?: PipelineState;
  currentPhase?: string;
  message: string;
  error?: string;
}

// v4.10 - PIPELINE-ORCHESTRATOR-SYNC-AWAIT-01: 409 not-ready response shape (stamp re-bumped from v4.09 to avoid collision with WRANGLING-L3 / Mobile-responsive release)
// Reasons mirror pipeline_readiness.ReadinessReason on d75-agent.
export type SetupNotReadyReason =
  | 'AIRBYTE_SETUP_PENDING'
  | 'AIRBYTE_SETUP_FAILED'
  | 'AIRBYTE_INITIAL_SYNC_PENDING'
  | 'AIRBYTE_SYNC_FAILED' // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6a, F82)
  | 'NO_CONNECTIONS'
  | 'TENANT_NOT_FOUND'
  | 'DB_ERROR'
  | 'WORKFLOW_NOT_ELIGIBLE' // v4.23 - CONNECTOR-HEALTH-STATE-H4 (H4-R2): onboarding not far enough along
  | 'INDUSTRY_CODE_MISSING' // v4.26 - INDUSTRY-CODE-NOT-STORED S8: no stored industry code (readiness v1.06)
  | 'UNKNOWN';

export interface SetupNotReadyResponse {
  status: 'not_ready';
  reason: SetupNotReadyReason;
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6e): `details` removed — the server no longer sends it
  connectionsPending?: string[];
  httpStatus?: number;
}

// v2.44 - Multi-phase pipeline status types
// v2.48 - error can be string or object with message - 2026-02-03
// v2.88 - Added awaiting_approval status, proposal count fields
export interface PhaseStatus {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'awaiting_approval';
  startedAt?: string;
  completedAt?: string;
  jobId?: string;
  error?: string | { message?: string; [key: string]: any };
  progress?: number;
  recordsSynced?: number;
  autoApproved?: number;
  pendingReview?: number;
  totalProposals?: number;
}

// v2.88 - Added approval phase and awaiting_approval status
export interface PipelineState {
  startedAt: string;
  lastUpdatedAt: string;
  currentPhase: string | null;
  overallStatus: 'running' | 'completed' | 'failed' | 'awaiting_approval';
  completedAt?: string;
  phases: {
    sync: PhaseStatus;
    discovery: PhaseStatus;
    approval: PhaseStatus;
    transform: PhaseStatus;
    gold: PhaseStatus;
  };
}

export interface SetupStatusResponse {
  status: string;
  tenantId: string;
  setupComplete: boolean;
  setupCompletedAt?: string;
  workflowState?: string;
  pipelineState?: PipelineState;
  // v2.88 - Added awaiting_approval to pipelineStatus, approval to phases
  pipelineStatus?: 'queued' | 'running' | 'completed' | 'failed' | 'awaiting_approval' | null;
  currentPhase?: string;
  phaseDescription?: string;
  overallProgress?: number;
  phases?: {
    sync: PhaseStatus;
    discovery: PhaseStatus;
    approval: PhaseStatus;
    transform: PhaseStatus;
    gold: PhaseStatus;
  };
  // v4.40 - BATCH-2026-10-31B R12 - 2026-11-01: the stable start-failure code only (the sentence is portal text)
  startFailure?: { reason: string };
}

export const setupService = {
  // Complete onboarding and trigger data pipeline.
  // v4.10 - PIPELINE-ORCHESTRATOR-SYNC-AWAIT-01: accept 409 (not-ready) so the caller can
  // inspect `reason` and decide retry vs surface. 5xx still throws.
  complete: async (): Promise<SetupCompleteResponse | SetupNotReadyResponse> => {
    const response = await api.post<SetupCompleteResponse | SetupNotReadyResponse>(
      '/setup/complete',
      undefined,
      { validateStatus: (status) => status < 500 }
    );
    if (response.status === 409) {
      // v4.27 - INDUSTRY-CODE-NOT-STORED FU-1: the API sends FastAPI's HTTPException body, which nests the
      // not-ready object under `detail`; read it from there, else from the flat body - 2026-10-07
      const raw: any = response.data;
      const body = (raw && typeof raw.detail === 'object' && raw.detail !== null ? raw.detail : raw) as SetupNotReadyResponse;
      return { ...body, status: 'not_ready', httpStatus: 409 };
    }
    return response.data;
  },

  // Get current setup/pipeline status for polling
  getStatus: async (): Promise<SetupStatusResponse> => {
    const response = await api.get<SetupStatusResponse>('/setup/status');
    return response.data;
  },

  // v2.88 - Resume pipeline after proposal approval
  resume: async (): Promise<{ status: string; message: string; pipelineState?: PipelineState }> => {
    const response = await api.post('/setup/resume');
    return response.data;
  }
};

// Discovery Reset Service (v2.34)
export interface DiscoveryResetResponse {
  status: string;
  tenantId: string;
  message: string;
  resetSessionCount: number;
}

// v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40 (option A2): "Skip for now" on Connect your data.
export interface ConnectSkipResponse {
  status: string;
  outcome: 'stored' | 'already_set';
  connectSkipped: boolean;
  requestId: string;
}

export const onboardingService = {
  connectSkip: async (): Promise<ConnectSkipResponse> => {
    const response = await api.post<ConnectSkipResponse>('/onboarding/connect-skip');
    return response.data;
  },
};

export const discoveryResetService = {
  // Reset discovery to allow redo
  reset: async (): Promise<DiscoveryResetResponse> => {
    const response = await api.post<DiscoveryResetResponse>('/discovery/reset');
    return response.data;
  }
};

// ============================================================================
// v11.13 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER (Branko M31077): the
// NaicsCandidate / NaicsPending / NaicsStatusResponse / NaicsResolveResponse
// types and the onboardingService.naicsStatus / naicsResolve calls are
// retired together with the backend endpoints /onboarding/naics-status,
// /onboarding/naics-resolve and /onboarding/confirm-naics. The customer
// picks their industry from the three Census 2022 dropdowns and the
// IndustryMatchPicker popup (BasicInfoForm.tsx + IndustryMatchPicker.tsx);
// basic-info stores the picked 6-digit code directly.
// ============================================================================

// ============================================================================
// Dashboard KPI Service - KPI cards and Power BI embed (v2.45)
// v2.45 - 2026-02-02 - Pre-computed KPIs from Cosmos, Power BI embedded dashboards
// ============================================================================

// KPI value from cache (per architecture doc Section 3.4)
// v4.10 - TRUST-KPI-01: per-card answer-confidence summary - 2026-04-28
export interface CardConfidence {
  score: number;
  tier:
    | 'high_confidence'
    | 'likely_correct'
    | 'needs_review'
    | 'conflicting_records'
    | 'insufficient_data';
  label: string;
  safe: boolean;
  override?: 'user_override';
}

// v4.10 - TRUST-KPI-01: tenant-wide data reliability score - 2026-04-28
export interface TenantReliability {
  score: number;
  label: 'Strong' | 'Mixed' | 'Weak';
  sources_count: number;
  suppressed_count: number;
  collusion_count: number;
}

export interface KPIValue {
  cardId: string;
  currentValue: number | null;
  previousValue: number | null;
  changePercent: number | null;
  changeDirection: 'up' | 'down' | 'flat';
  sparkline: number[];
  sparklinePeriods: string[];  // e.g., ["Oct", "Nov", "Dec", "Jan", "Feb"]
  asOfDate: string;
  error?: string | null;
  // v4.10 - TRUST-KPI-01: only present when trust_engine flag is "true"|"live"
  // AND tenant lineage coverage ≥ 50%. Components must treat as optional.
  answer_confidence?: CardConfidence | null;
}

// KPI card definition (per architecture doc Section 3.1)
// v2.91 - Added origin fields for future AI chat card creation - 2026-02-07
export interface KPICardDefinition {
  cardId: string;
  title: string;
  description: string;
  goldTable: string;
  valueColumn: string;
  format: 'currency' | 'number' | 'percentage' | 'duration';  // v4.03 - STORY-142: added duration
  formatOptions?: {
    currency?: string;
    decimals?: number;
    abbreviate?: boolean;
    unit?: string;  // v4.03 - STORY-142: time unit for duration ("days"|"hours"|"minutes")
  };
  unit?: string;           // v4.03 - STORY-142: raw unit label ("currency"|"%"|"days"|"hours"|"minutes"|"count"|"units"|"")
  semanticType?: string;   // v4.03 - STORY-143: used to gate sparkline display
  trendColumn?: string;
  trendDirection: 'up_is_good' | 'down_is_good';
  sparklineQuery?: string;
  category: string;
  position: number;
  icon: string;  // MUI icon name
  color: string;  // Hex color for card accent
  thresholds?: {
    warning: number;
    critical: number;
  };
  isDefault: boolean;
  origin: 'pipeline' | 'ai_chat' | 'manual';              // v2.91
  createdVia: 'pipeline_auto' | 'dashboard_initialize' | 'chat_request'; // v2.91
  requestedBy: string | null;                              // v2.91
  requestPrompt: string | null;                            // v2.91
  removable: boolean;                                      // v2.91
  pinned: boolean;                                         // v2.91
  createdAt: string | null;                                // v2.91
}

// Dashboard layout widget position
export interface WidgetPosition {
  row: number;
  col: number;
  width: number;
  height: number;
}

// Dashboard widget configuration
export interface DashboardWidget {
  type: 'kpi_card';
  cardId?: string;
  position: WidgetPosition;
}

// Dashboard filter configuration
export interface DashboardFilter {
  field: string;
  type: 'date_range' | 'dropdown' | 'text';
  default: string;
  table?: string;
}

// Dashboard layout configuration
export interface DashboardLayout {
  type: 'grid';
  columns: number;
  widgets: DashboardWidget[];
}

// Dashboard registry entry
// v3.32 (2026-02-23): Removed Superset fields (ADR-002: Plotly migration)
export interface DashboardInfo {
  dashboardId: string;
  name: string;
  description: string;
  goldTables: string[];
  linkedCards: string[];
  category: string;
  position: number;
  icon: string;
  layout: DashboardLayout;
  filters: DashboardFilter[];
  isDefault: boolean;
}

// User dashboard config (per architecture doc Section 3.3)
export interface DashboardConfig {
  cardOrder: string[];  // Array of cardIds
  hiddenCards: string[];
  pinnedDashboards: string[];
  defaultDashboard: string;
  theme: 'light' | 'dark';
  layout?: Array<{ cardId: string; x: number; y: number; w: number; h: number }>; // v2.96 - Grid layout positions
}

// Response from GET /api/dashboard/kpis
export interface DashboardKPIsResponse {
  success: boolean;
  tenantId: string;
  computedAt?: string;
  kpis: Record<string, KPIValue>;  // Keyed by metric name
  cacheStatus: 'fresh' | 'stale' | 'empty';
  cacheAgeHours?: number;
  message?: string;
  errors?: string[];
  // v4.10 - TRUST-KPI-01: only present when trust_engine flag is "true"|"live"
  data_reliability?: TenantReliability | null;
}

// Response from GET /api/dashboard/cards
export interface DashboardCardsResponse {
  success: boolean;
  tenantId: string;
  cards: KPICardDefinition[];
}

// Response from GET /api/dashboard/list
export interface DashboardListResponse {
  success: boolean;
  tenantId: string;
  dashboards: DashboardInfo[];
}

// Response from GET/PUT /api/dashboard/config
export interface DashboardConfigResponse {
  success: boolean;
  tenantId: string;
  config: DashboardConfig;
}

// Request for PUT /api/dashboard/cards/order
export interface CardOrderRequest {
  cardOrder: string[];  // Array of cardIds in display order
}

// Request for PUT /api/dashboard/config
export interface DashboardConfigUpdateRequest {
  cardOrder?: string[];
  hiddenCards?: string[];
  pinnedDashboards?: string[];
  defaultDashboard?: string;
  theme?: 'light' | 'dark';
  layout?: Array<{ cardId: string; x: number; y: number; w: number; h: number }>; // v2.96 - Grid layout positions
}

// v3.19 - Recommendation types for AI Insights panel
// v3.86 - Story 19 PLG: added free + body fields for locked insight rendering
// v3.90 - W3-1-Step3: RagChunkRef + WOW fields (optional -- absent on pre-Step-2 rows)
// v3.92 - W3-1-F3: chunk_excerpt_text added to RagChunkRef (industry RAG content, surfaceable)

/** Wire-safe RAG chunk reference -- server strips internal identifiers before sending. */
export interface RagChunkRef {
  retrieval_method: string;
  relevance_score: number;
  // v3.92 - F3: short human-readable excerpt from industry knowledge base (max 200 chars)
  chunk_excerpt_text?: string;
}

export interface Recommendation {
  id: string;
  type: 'opportunity' | 'warning' | 'success' | 'insight';
  title: string;
  description: string;
  body?: string;              // v3.86 - locked insight redacted body text
  impact: 'High' | 'Medium' | 'Low';
  // v3.94 - W2-Session301 Item 16: nullability matches the real wire result. D75 nulls
  // estimatedValue and estimatedValueLabel (and strips estimatedValue) for LOCKED insights;
  // only entitled/free insights carry authoritative numbers.
  estimatedValue: number | null;
  estimatedValueLabel: string | null;
  confidence: number;
  category: string;
  relatedKpiCardId: string | null;
  discoveryNeed: string | null;
  methodology: string | null;
  createdAt: string | null;
  free?: boolean;             // v3.86 - true = full insight, false = redacted (locked)
  // v3.93 - W2-Session301 Item 16: dollarValue/percentChange removed. They had no
  // authoritative producer (pipeline persists estimatedValue/estimatedValueLabel; D75
  // never populated dollarValue/percentChange) and no UI consumer. The wire contract
  // exposes numbers only via estimatedValue/estimatedValueLabel (authoritative source).
  // v3.90 - W3-1-Step3: WOW RAG signal fields (optional — absent on pre-Step-2 rows)
  isWow?: boolean;
  hierarchyTier?: number;
  tierLabel?: string;
  wowSourceChunks?: RagChunkRef[];
}

export interface RecommendationsResponse {
  success: boolean;
  tenantId: string;
  count: number;
  recommendations: Recommendation[];
  // v3.87 - Fix 1: flag returned by backend so InsightsPanel X-dismiss can gate Screen 2
  payPerQuestionEnabled?: boolean;
}

// v3.55 - Story 12A.1: Chart types moved to chartUtils.ts, re-exported for backward compat - 2026-02-24
export type { ColumnDefinition, VisualizationMetadata, ChartReportData };

export interface ChartDataResponse {
  success: boolean;
  reports: ChartReportData[];
}

export const dashboardKPIService = {
  // Get all KPI values from cache (cached in Cosmos, computed by pipeline)
  getKPIs: async (): Promise<DashboardKPIsResponse> => {
    const response = await api.get<DashboardKPIsResponse>('/dashboard/kpis');
    return response.data;
  },

  // Get KPI card definitions
  getCards: async (): Promise<DashboardCardsResponse> => {
    const response = await api.get<DashboardCardsResponse>('/dashboard/cards');
    return response.data;
  },

  // Update card display order
  updateCardOrder: async (cardOrder: string[]): Promise<{ success: boolean }> => {
    const response = await api.put('/dashboard/cards/order', { cardOrder });
    return response.data;
  },

  // Get list of available dashboards
  getDashboards: async (): Promise<DashboardListResponse> => {
    const response = await api.get<DashboardListResponse>('/dashboard/list');
    return response.data;
  },

  // Get user dashboard preferences
  getConfig: async (): Promise<DashboardConfigResponse> => {
    const response = await api.get<DashboardConfigResponse>('/dashboard/config');
    return response.data;
  },

  // Update user dashboard preferences
  updateConfig: async (config: DashboardConfigUpdateRequest): Promise<DashboardConfigResponse> => {
    const response = await api.put<DashboardConfigResponse>('/dashboard/config', config);
    return response.data;
  },

  // v2.91 - Card management for future AI chat - 2026-02-07
  createCard: async (card: { title: string; goldTable: string; valueColumn: string;
    format?: string; origin?: string; requestedBy?: string; requestPrompt?: string; }) => {
    const response = await api.post('/dashboard/cards', card);
    return response.data;
  },
  updateCard: async (cardId: string, updates: { pinned?: boolean; position?: number; title?: string; }) => {
    const response = await api.patch(`/dashboard/cards/${cardId}`, updates);
    return response.data;
  },
  deleteCard: async (cardId: string) => {
    const response = await api.delete(`/dashboard/cards/${cardId}`);
    return response.data;
  },

  // v3.19 - Get active recommendations for AI Insights panel
  getRecommendations: async (): Promise<RecommendationsResponse> => {
    const response = await api.get<RecommendationsResponse>('/dashboard/recommendations');
    return response.data;
  },

  // v3.33 - Batched chart data for Plotly migration (ADR-002) - 2026-02-23
  getChartData: async (): Promise<ChartDataResponse> => {
    const response = await api.get<ChartDataResponse>('/dashboard/chart-data');
    return response.data;
  },

  // v4.05 - [185]: Trigger gold-only pipeline refresh — direct to d75-agent via Front Door - 2026-04-26
  dashboardRefresh: async (tenantId: string): Promise<{ ok: boolean }> => {
    const response = await api.post<{ ok: boolean }>(`/aggregate/${tenantId}`);
    return response.data;
  },
};

// ============================================================================
// Proposal Service - Proposal review and approval (v2.88)
// v2.88 - 2026-02-06 - New service for proposal approval flow
// ============================================================================

// v2.90 - Updated to include both enriched and NB05 raw field names
export interface Proposal {
  id: string;
  tenantId: string;
  docType: string;
  title: string;
  // Frontend-preferred fields (populated after AI enrichment)
  sourceTable?: string;
  targetTable?: string;
  operation?: string;
  autoApprovable?: boolean;
  laymanExplanation?: string;
  risks?: string[];
  // NB05 raw fields (before enrichment, used as fallbacks)
  sourceType?: string;          // v2.90 - Raw NB05 field, mapped to sourceTable
  proposalType?: string;        // v2.90 - Raw NB05 field, mapped to operation
  requiresReview?: boolean;     // v2.90 - Raw NB05 field, inverse of autoApprovable
  explanation?: string;         // v2.90 - Raw NB05 field, fallback for laymanExplanation
  // Common fields
  status: 'pending' | 'approved' | 'rejected' | 'superseded' | 'needs_review'; // v3.04 - Added superseded; v1.87 - Added needs_review (adversarial escalation — SAP escape)
  riskLevel?: 'low' | 'medium' | 'high';
  confidence?: number;
  recommendation?: string;
  pros?: string[];
  cons?: string[];
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt?: string;
  enrichedAt?: string;          // v2.90 - When AI enrichment was applied
  enrichmentVersion?: number;   // v2.90 - Enrichment prompt version
  sampleBefore?: string[];      // v3.00 - Sample data before transformation
  sampleAfter?: string[];       // v3.00 - Sample data after transformation
  proposalDomain?: string;      // v3.03 - Domain letter (A-I) for hierarchical grouping
  entityType?: string;          // v3.03 - Entity type for additional context
  affectedColumn?: string;      // v3.04 - Column affected by the proposal
  // v3.04 - Smart review fields
  groupKey?: string | null;
  isRecommended?: boolean;
  alternativeCount?: number;
  userCategory?: string;
  userSubcategory?: string;
  supersededAt?: string;
  supersededBy?: string;
  supersededReason?: string;
  // v1.87 - SAP US12561297B2 Escape: adversarial Generator-Critic loop output.
  // Present when status === 'needs_review' (reviewer exhausted MAX_RETRIES with confidence < CONFIDENCE_THRESHOLD).
  // Exposes generator instruction + reviewer critique for human decision in ProposalReview.tsx.
  aiDecision?: {
    instruction?: {
      operation: string;              // One of 13 operation families (generative, not bounded enum)
      target_columns: string[];
      parameters: Record<string, unknown>;
      rationale: string;
    };
    confidence?: number;              // Last reviewer confidence score (0-100)
    concerns?: string[];              // Specific issues raised by adversarial reviewer
    justification?: string;           // Reviewer 2-3 sentence scoring explanation
    needsReview?: boolean;            // True when adversarial loop exhausted MAX_RETRIES
  };
}

// v2.89 - Fixed: backend returns "proposals" not "mappings"
export interface ProposalsResponse {
  status: string;
  tenantId: string;
  tenant_id?: string;
  proposals: Proposal[];
  mappings?: Proposal[]; // Legacy fallback
  total: number;
}

export interface ProposalStatsResponse {
  status: string;
  tenantId: string;
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  byRisk?: {
    high: number;
    medium: number;
    low: number;
  };
}

export interface ApprovalModeResponse {
  approval_mode: 'auto' | 'smart_default' | 'full_review';
  medium_risk_auto_approve: boolean;
}

// v3.05 - Smart Proposal Review types (lazy-loading)
export interface DecisionBundle {
  groupKey: string;
  column: string;
  sourceType: string;
  entityType: string;
  alternatives: Proposal[];
  recommendedId: string | null;
}

// v3.05 - Counts only (no proposal objects in summary)
export interface SubcategorySummary {
  label: string;
  autoHandledCount: number;
  needsReviewCount: number;
  bundleCount: number;
  standaloneCount: number;
  highRiskCount: number;
}

export interface CategorySummary {
  label: string;
  subcategories: Record<string, SubcategorySummary>;
  autoHandledCount: number;
  needsReviewCount: number;
  totalCount: number;
}

export interface ProposalSummaryResponse {
  tenant_id: string;
  categories: Record<string, CategorySummary>;
  totalAutoHandled: number;
  totalNeedsReview: number;
  totalProposals: number;
}

// v3.05 - Subcategory detail response (lazy-loaded on L2 expand)
// v3.08 - Added statusFilter field
export interface SubcategoryDetailResponse {
  category: string;
  subcategory: string;
  statusFilter?: string;
  decisionBundles: DecisionBundle[];
  standalone: Proposal[];
  totalItems: number;
}

// v3.07 - Added all_pending, category_pending, subcategory_pending filter types for bulk reject/category actions
export interface BulkUpdateRequest {
  filter_type: 'all' | 'ids' | 'groupKey' | 'recommended_pending' | 'subcategory' | 'all_pending' | 'category_pending' | 'subcategory_pending';
  filter_value?: string;
  action: 'approve' | 'reject' | 'supersede' | 'apply_bundle';
  reason?: string;
  selected_id?: string;
}

export interface BulkUpdateResponse {
  success: boolean;
  updated: number;
  action: string;
  column_mappings_created: number;
}

// v3.06 - Semantic Strategy Selection types
export interface AlternativeStrategy {
  techniqueId: string;
  displayName: string;
  description: string;
  riskLevel: string;
  requiresReview: boolean;
  isCurrent: boolean;
  isInvalid: boolean;
  invalidReason?: string;
}

export interface ProposalAlternativesResponse {
  proposalId: string;
  issueType: string | null;
  currentStrategy: string;
  aiReason: string;
  aiConfidence: number | null;
  dataType: string;
  alternatives: AlternativeStrategy[];
}

export interface ProposalOverrideResponse {
  proposalId: string;
  previousStrategy: string;
  newStrategy: string;
  status: string;
  message: string;
}

export const proposalService = {
  // List proposals (optionally filter by status)
  list: async (params?: { status?: string; docType?: string }): Promise<ProposalsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.docType) queryParams.append('docType', params.docType);
    const qs = queryParams.toString();
    const response = await api.get<ProposalsResponse>(`/proposals${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Approve proposals by IDs
  approve: async (mappingIds: string[]): Promise<{ status: string; approved: number }> => {
    const response = await api.post('/proposals/approve', { mapping_ids: mappingIds });
    return response.data;
  },

  // Reject proposals by IDs with reason
  reject: async (mappingIds: string[], reason: string): Promise<{ status: string; rejected: number }> => {
    const response = await api.post('/proposals/reject', { mapping_ids: mappingIds, reason });
    return response.data;
  },

  // Get proposal stats
  getStats: async (): Promise<ProposalStatsResponse> => {
    const response = await api.get<ProposalStatsResponse>('/proposals/stats');
    return response.data;
  },

  // v3.04 - Smart Proposal Review
  getSummary: async (): Promise<ProposalSummaryResponse> => {
    const response = await api.get<ProposalSummaryResponse>('/proposals/summary');
    return response.data;
  },

  bulkUpdate: async (request: BulkUpdateRequest): Promise<BulkUpdateResponse> => {
    const response = await api.post<BulkUpdateResponse>('/proposals/bulk-update', request);
    return response.data;
  },

  // v3.05 - Lazy load proposals for a specific subcategory
  // v3.08 - Added status_filter: "pending" (default), "handled", or "all"
  getSubcategoryProposals: async (
    category: string,
    subcategory: string,
    statusFilter: string = 'pending'
  ): Promise<SubcategoryDetailResponse> => {
    const response = await api.get<SubcategoryDetailResponse>(
      `/proposals/subcategory?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}&status_filter=${encodeURIComponent(statusFilter)}`
    );
    return response.data;
  },

  // v3.06 - Semantic Strategy Selection: get alternative strategies
  getAlternatives: async (proposalId: string): Promise<ProposalAlternativesResponse> => {
    const response = await api.get<ProposalAlternativesResponse>(
      `/proposals/${encodeURIComponent(proposalId)}/alternatives`
    );
    return response.data;
  },

  // v3.06 - Override proposal strategy
  overrideStrategy: async (proposalId: string, newStrategy: string, reason?: string): Promise<ProposalOverrideResponse> => {
    const response = await api.post<ProposalOverrideResponse>(
      `/proposals/${encodeURIComponent(proposalId)}/override`,
      { newStrategy, reason: reason || '' }
    );
    return response.data;
  },
};

export const approvalModeService = {
  // Get current approval mode
  get: async (): Promise<ApprovalModeResponse> => {
    const response = await api.get<ApprovalModeResponse>('/settings/approval-mode');
    return response.data;
  },

  // Update approval mode
  update: async (approvalMode: string, mediumRiskAutoApprove?: boolean): Promise<ApprovalModeResponse> => {
    const response = await api.patch<ApprovalModeResponse>('/settings/approval-mode', {
      approval_mode: approvalMode,
      medium_risk_auto_approve: mediumRiskAutoApprove
    });
    return response.data;
  }
};

// v2.94 - Notification Center
export interface Notification {
  id: string;
  tenantId: string;
  type: 'pipeline_complete' | 'sync_error' | 'team_invite' | 'deletion_warning' | 'webhook_disabled' | 'system';
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  createdAt: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
}

export interface UnreadCountResponse {
  count: number;
}

export const notificationService = {
  // v2.94 - Get all notifications (optionally unread only)
  getAll: async (unreadOnly: boolean = false, limit: number = 50): Promise<NotificationListResponse> => {
    const response = await api.get<NotificationListResponse>(
      `/notifications?unread_only=${unreadOnly}&limit=${limit}`
    );
    return response.data;
  },

  // v2.94 - Lightweight unread count for polling
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get<UnreadCountResponse>('/notifications/count');
    return response.data.count;
  },

  // v2.94 - Mark single notification as read
  markRead: async (notificationId: string): Promise<void> => {
    await api.patch(`/notifications/${notificationId}/read`);
  },

  // v2.94 - Mark all notifications as read
  markAllRead: async (): Promise<void> => {
    await api.post('/notifications/mark-all-read');
  },
};

// v1.0 - W3-1-F1: Degradation banner types + service (2026-07-02)
export interface Banner {
  key: string;        // 'pipeline_failure' | 'data_stale'
  severity: 'error' | 'warning';
  title: string;
  body: string;
  phase?: string;
  created_at?: string;
}

export interface BannersResponse {
  banners: Banner[];
  tenant_id: string;
}

export const bannerService = {
  // v1.0 - W3-1-F1: Fetch audience-filtered degradation banners (polled every 30s from Dashboard.tsx)
  getBanners: async (): Promise<BannersResponse> => {
    const response = await api.get<BannersResponse>('/banners');
    return response.data;
  },
};

// v2.97 - Report Builder
export interface Report {
  id: string;
  tenantId: string;
  title: string;
  description?: string;
  goldTable: string;
  columns: string[];
  filters: Array<{ column: string; operator: string; value: any }>;
  sortBy?: string;
  sortDirection: string;
  chartType: string;
  createdAt: string;
  createdBy?: string;
  // v4.28 - G10-B (final G10-A contract, gap 2): the VERIFIED creator's Clerk user id; null for older and auto reports
  createdByUserId?: string | null;
  // v3.22 - Auto-generated report fields
  origin?: string;           // "auto" | "user"
  pipelineRunId?: string;
  sourceRuleId?: string;
  category?: string;
  relatedKpiCardId?: string;
  relevanceScore?: number;
  previewImageUrl?: string | null;  // v3.69 - VIZ-01: thumbnail URL for report card
}

// v4.28 - G10-B (D1): who may delete a report. The company owner may delete any report; anyone else only a report they
// created. The server enforces the same rule; this only decides whether the Delete control is shown. A report with no
// verified creator (older or auto-generated) is deletable by the owner only.
export const canDeleteReport = (
  report: Pick<Report, 'createdByUserId'>,
  me: { userId: string | null; isOwnerOfCompany: boolean; mayManageOwnReports: boolean },
): boolean =>
  me.mayManageOwnReports && (me.isOwnerOfCompany || (!!me.userId && !!report.createdByUserId && report.createdByUserId === me.userId));

export interface ReportListResponse {
  success: boolean;
  reports: Report[];
  total: number;
}

export interface ReportDataResponse {
  success: boolean;
  reportId: string;
  goldTable: string;
  columns: Array<{ name: string; type: string }>;
  data: any[];
  total: number;
}

export const reportService = {
  // v2.97 - Create a report
  create: async (report: Omit<Report, 'id' | 'tenantId' | 'createdAt'>): Promise<{ success: boolean; report: Report }> => {
    const response = await api.post('/reports', report);
    return response.data;
  },

  // v2.97 - List all reports
  getAll: async (): Promise<ReportListResponse> => {
    const response = await api.get<ReportListResponse>('/reports');
    return response.data;
  },

  // v2.97 - Get single report
  get: async (id: string): Promise<{ success: boolean; report: Report }> => {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  },

  // v2.97 - Get report data
  getData: async (id: string, limit: number = 100, offset: number = 0): Promise<ReportDataResponse> => {
    const response = await api.get<ReportDataResponse>(`/reports/${id}/data?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  // v2.97 - Delete report
  delete: async (id: string): Promise<void> => {
    await api.delete(`/reports/${id}`);
  },
};

// ============================================================================
// WEBHOOK SERVICE (v2.98)
// ============================================================================

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
  lastTriggeredAt?: string;
  failureCount: number;
  secret?: string; // Only returned on creation
}

export interface WebhookListResponse {
  webhooks: Webhook[];
  total: number;
}

export interface WebhookEvent {
  event: string;
  description: string;
}

export interface DeliveryLog {
  id: string;
  webhookId: string;
  event: string;
  statusCode?: number;
  success: boolean;
  error?: string;
  timestamp: string;
}

export interface DeliveryLogsResponse {
  logs: DeliveryLog[];
  total: number;
}

export const webhookService = {
  // v2.98 - Create webhook
  create: async (webhook: { name: string; url: string; events: string[] }): Promise<{ success: boolean; webhook: Webhook }> => {
    const response = await api.post('/webhooks', webhook);
    return response.data;
  },

  // v2.98 - List webhooks
  getAll: async (): Promise<WebhookListResponse> => {
    const response = await api.get<WebhookListResponse>('/webhooks');
    return response.data;
  },

  // v2.98 - Update webhook
  update: async (id: string, data: { name?: string; url?: string; events?: string[]; active?: boolean }): Promise<{ success: boolean; webhook: Webhook }> => {
    const response = await api.patch(`/webhooks/${id}`, data);
    return response.data;
  },

  // v2.98 - Delete webhook
  delete: async (id: string): Promise<void> => {
    await api.delete(`/webhooks/${id}`);
  },

  // v2.98 - Test webhook
  test: async (id: string): Promise<{ success: boolean; statusCode?: number; error?: string }> => {
    const response = await api.post(`/webhooks/${id}/test`);
    return response.data;
  },

  // v2.98 - Get delivery logs
  getLogs: async (id: string, limit: number = 50): Promise<DeliveryLogsResponse> => {
    const response = await api.get<DeliveryLogsResponse>(`/webhooks/${id}/logs?limit=${limit}`);
    return response.data;
  },

  // v2.98 - Get available event types
  getEvents: async (): Promise<{ events: WebhookEvent[] }> => {
    const response = await api.get<{ events: WebhookEvent[] }>('/webhooks/events');
    return response.data;
  },
};

// ============================================================================
// Connector Health Service (v3.00)
// v3.00 - 2026-02-07 - Connector health status and support escalation
// ============================================================================

// v4.22 - 2026-10-02 - CONNECTOR-HEALTH-STATE-H3 (H3-9; F85): ConnectorHealthStatus / ConnectorHealthResponse and
// the per-connector-type getHealth call are removed with the retired summary route; connection health is
// healthState / healthReason on each connection (H2).

export interface SupportTicket {
  id: string;
  tenantId: string;
  connectorType: string;
  connectionId: string;
  status: 'open' | 'acknowledged' | 'resolved';
  reportedAt: string;
  resolvedAt?: string;
}

export const connectorHealthService = {
  // v3.00 - Report an issue with a connector
  reportIssue: async (connectionId: string): Promise<{ success: boolean; ticketId: string }> => {
    const response = await api.post(`/connections/${connectionId}/report-issue`);
    return response.data;
  },

  // v3.00 - Get health history for a connection
  getHealthHistory: async (connectionId: string): Promise<{ logs: any[] }> => {
    const response = await api.get(`/connections/${connectionId}/health-history`);
    return response.data;
  },
};

// v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2 (H2-4, F84): the v3.66 AIR-04 per-type Airbyte sync status
// service and its response type are removed, with the chip that used them and its BFF function. Connection
// health is healthState / healthReason on the connection itself.

// ---------------------------------------------------------------------------
// v3.60 - NLP Chat Service - 2026-03-03
// Talks to /api/nlp/* BFF proxies → d75-agent → pipeline-runner /api/chat
// ---------------------------------------------------------------------------

export interface NlpClarifier {
  question: string;
  options: string[];
  allows_multiple: boolean;
}

export interface NlpCitation {
  claim: string;
  source_value: string;
  source_table: string;
  source_column: string;
}

export interface NlpChatRequest {
  message: string;
  session_id?: string;
  clarifier_context_id?: string;
  selected_clarifiers?: string[];
  // v3.83 - NLP-02: set true to bypass boundary check and answer via general LLM
  override?: boolean;
}

export interface NlpChatResponse {
  session_id: string;
  message: string;
  citations?: NlpCitation[];
  clarifiers?: NlpClarifier[];
  clarifier_context_id?: string;
  cache_hit?: boolean;
  latency_ms?: number;
  pii_detected?: boolean;
  tokens_used?: Record<string, number>;
  // v3.77 - SHOW-01: trace entity ID for "How we found this" panel (replaces WOW Cascade fields)
  message_id?: string;
  // v3.83 - NLP-02: boundary detection fields
  boundary_level?: 'WARN' | 'REJECT';
  boundary_message?: string;
  // v3.83 - NLP-02: general knowledge override response fields
  is_general_knowledge?: boolean;
  disclaimer?: string;
  override_used?: boolean;
  // v3.94 - MISSED-ID-04 EDGS: employment decision risk fields
  risk_level?: string;
  disclaimer_text?: string;
  requires_manual_review?: boolean;
  // v4.04 - STORY-171: answer consistency change notification
  answer_changed?: boolean;
  divergence_pct?: number;
  // v4.07 - AUDIT-12: inline confidence badge — backend already sends this field
  confidence_label?: string;
}

export interface NlpSessionMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface NlpSession {
  session_id: string;
  messages: NlpSessionMessage[];
  created_at: string;
  last_active_at: string;
}

export interface NlpFeedbackRequest {
  session_id: string;
  message_index: number;
  rating: number; // 1-5
  comment?: string;
}

export const nlpChatService = {
  // v3.60 - Send a message to the NLP pipeline
  sendMessage: async (request: NlpChatRequest): Promise<NlpChatResponse> => {
    const response = await api.post<NlpChatResponse>('/nlp/chat', request);
    return response.data;
  },

  // v3.60 - Get session history
  getSession: async (sessionId: string): Promise<NlpSession> => {
    const response = await api.get<NlpSession>(`/nlp/sessions/${sessionId}`);
    return response.data;
  },

  // v3.60 - Delete (end) a session
  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/nlp/sessions/${sessionId}`);
  },

  // v3.60 - Submit feedback on a response
  submitFeedback: async (request: NlpFeedbackRequest): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>('/nlp/feedback', request);
    return response.data;
  },
};

// ── v3.77 - SHOW-01: Trace service ──────────────────────────────────────────

export interface TraceData {
  entity_type: string;
  entity_id: string;
  confidence_score: number;
  confidence_label: string;       // "Strong signal" | "Good indication" | "Early signal"
  summary_headline: string;
  how_we_found_it: string;
  data_sources_used: string[];
  time_period: string;
  pipeline_run_id?: string | null;
}

export const traceService = {
  /** Fetch the trace record for one AI output. Returns null when none exists (404). */
  getTrace: async (entityType: string, entityId: string): Promise<TraceData | null> => {
    try {
      const response = await api.get<TraceData>(`/trace/${entityType}/${entityId}`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },
};

// ---------------------------------------------------------------------------
// v3.99 - UNSTRUCTURED-01: Document ingestion service - 2026-04-19
// ---------------------------------------------------------------------------

export interface DocumentUploadResponse {
  session_id: string;
  status: string;
  filename: string;
  file_size_bytes?: number;
  page_count_estimated?: number;
  duplicate: boolean;
  // v3.0 — Phase 4: true when this upload draws from the top-up pool
  topup_notice?: boolean;
  upload_source?: string;
  message: string;
}

export interface DocumentStatusResponse {
  session_id: string;
  filename: string;
  status: string;  // queued | processing | bronze_ready | failed | error
  page_count_estimated?: number;
  page_count_actual?: number;
  element_count?: number;
  table_element_count?: number;
  error_message?: string;
  bronze_jsonl_path?: string;
  created_at: string;
  completed_at?: string;
  processing_time_ms?: number;
  retry_count: number;
}

export interface DocumentListItem {
  session_id: string;
  filename: string;
  file_size_bytes?: number;
  status: string;
  page_count_estimated?: number;
  page_count_actual?: number;
  element_count?: number;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

export interface PageUsage {
  period_month: string;
  allowance_pages: number;
  pages_used: number;
  pages_reserved: number;
  topup_pages_remaining: number;
  pages_remaining: number;
}

export interface DocumentListResponse {
  documents: DocumentListItem[];
  total: number;
  page_usage: PageUsage;
  stale_warning: boolean;
}

export const ingestService = {
  // v4.25 - SCREENS-301: ingest paths realigned to the backend namespace
  //         (/api/ingest/document/*) now that Front Door routes /api/* direct to d75-agent
  //         and the BFF proxies are off-path. Previous paths returned 404. - 2026-10-05

  /** Upload a document file for async processing. Returns 202 on success. */
  uploadDocument: async (file: File): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<DocumentUploadResponse>(
      "/ingest/document/upload",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  /** Poll status of a single upload session. */
  getDocumentStatus: async (sessionId: string): Promise<DocumentStatusResponse> => {
    const response = await api.get<DocumentStatusResponse>(`/ingest/document/status/${sessionId}`);
    return response.data;
  },

  /** List all document sessions for the current tenant. */
  listDocuments: async (
    statusFilter?: string,
    limit = 50,
    offset = 0
  ): Promise<DocumentListResponse> => {
    const params: Record<string, string | number> = { limit, offset };
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get<DocumentListResponse>("/ingest/document/list", { params });
    return response.data;
  },

  /** Phase 2 stub — returns empty history. */
  getDocumentHistory: async (): Promise<{ history: unknown[]; message: string }> => {
    const response = await api.get("/ingest/document/history");
    return response.data;
  },

  // v4.00 - UNSTRUCTURED-01 Phase 2: Cloud picker uploads - 2026-04-19

  /** Upload a document sourced from OneDrive via pre-authenticated download URL. */
  uploadFromOneDrive: async (payload: {
    download_url: string;
    filename: string;
    file_size_bytes?: number;
  }): Promise<DocumentUploadResponse> => {
    const response = await api.post<DocumentUploadResponse>(
      "/ingest/document/upload-from-onedrive",
      payload
    );
    return response.data;
  },

  /** Upload a document sourced from Google Drive via file ID + short-lived access token. */
  uploadFromGoogleDrive: async (payload: {
    file_id: string;
    access_token: string;
    filename: string;
    file_size_bytes?: number;
  }): Promise<DocumentUploadResponse> => {
    const response = await api.post<DocumentUploadResponse>(
      "/ingest/document/upload-from-googledrive",
      payload
    );
    return response.data;
  },
};

// v4.06 - [174]/[92]: Source Trust, Resolution Log, Cleaning Summary - 2026-04-26
export const trustService = {
  /** Effective trust ranks for the tenant (overrides + sentinel defaults). */
  getRanks: async (tenantId: string): Promise<{
    ranks: { connector: string; rank: number; is_override: boolean; updated_at: string | null }[];
  }> => {
    const response = await api.get(`/trust/ranks/${tenantId}`);
    return response.data;
  },

  /** Upsert a tenant trust rank override for a connector (rank 1–10). */
  updateRank: async (tenantId: string, connector: string, rank: number): Promise<{ ok: boolean }> => {
    const response = await api.put<{ ok: boolean }>(`/trust/ranks/${tenantId}/${connector}`, { rank });
    return response.data;
  },

  /** Paginated cross-source resolution audit log. */
  getResolutionLog: async (
    tenantId: string,
    params?: {
      from_date?: string;
      to_date?: string;
      connector_name?: string;
      match_tier?: number;
      resolution?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    rows: {
      id: number;
      run_id: string;
      entity_type: string;
      source_winner: string;
      source_loser: string;
      winner_rank: number;
      loser_rank: number;
      business_key: string | null;
      resolution: string;
      match_tier: number;
      conflict_fields: string[];
      created_at: string | null;
    }[];
    total: number;
    limit: number;
    offset: number;
  }> => {
    const response = await api.get(`/trust/resolution-log/${tenantId}`, { params });
    return response.data;
  },

  /** Plain-English cleaning summary from the most recent pipeline run. */
  getCleaningSummary: async (tenantId: string): Promise<{
    stats: { technique_id: string; label: string; count: number }[];
    pipeline_run_id: string | null;
    started_at: string | null;
    total_rows: number;
    message: string | null;
  }> => {
    const response = await api.get(`/transparency/cleaning-summary/${tenantId}`);
    return response.data;
  },

  // v4.09 - WRANGLING-L3: Resolution state counts from trust_proposals - 2026-04-26
  /** Resolution state counts (auto_resolved / needs_review / escalated) from trust_proposals. */
  getProposalsSummary: async (tenantId: string): Promise<{
    auto_resolved: number;
    needs_review: number;
    escalated: number;
    total: number;
    run_id: string | null;
    as_of: string | null;
  }> => {
    const response = await api.get(`/trust/proposals/summary/${tenantId}`);
    return response.data;
  },

  // v4.15 - TRUST-PROPOSALS-UI: per-proposal drill-down list + manual override - 2026-07-12
  /** Paginated per-proposal list from trust_proposals (confidence, state, collusion, override). */
  getProposals: async (
    tenantId: string,
    params?: {
      bucket?: 'auto_resolved' | 'needs_review' | 'escalated';
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    rows: {
      id: number;
      entity_type: string;
      field_name: string;
      proposed_value: string | null;
      confidence: number | null;
      winning_source: string | null;
      collusion_detected: boolean;
      status: string | null;
      resolution_state: string | null;
      bucket: 'auto_resolved' | 'needs_review' | 'escalated';
      overridden: boolean;
      created_at: string | null;
    }[];
    total: number;
    limit: number;
    offset: number;
  }> => {
    const response = await api.get(`/trust/proposals/${tenantId}`, { params });
    return response.data;
  },

  /** Manually override a trust proposal (stamps metadata.override='manual'). */
  overrideProposal: async (
    tenantId: string,
    proposalId: number
  ): Promise<{ ok: boolean; id: number; status: string | null; overridden: boolean }> => {
    const response = await api.patch(`/trust/proposals/${tenantId}/${proposalId}/override`);
    return response.data;
  },
};

export { api };
export const getToken = async (): Promise<string | null> =>
  getClerkToken ? getClerkToken() : null;

export default api;
